/* ============================================================
 *  Rich article model used in admin editor + public renderer
 *  Replaces the legacy 5-paragraph plain-text field.
 * ============================================================ */

export type AlertVariant = "info" | "warning" | "note";

export type CollapsibleProps = {
  collapsible?: boolean;
  collapseTitle?: string;
  collapseTitle_en?: string;
  collapseTitle_fr?: string;
};

export type ParagraphBlock = {
  id: string;
  type: "paragraph";
  /** Default (Arabic) content. */
  html: string;
  html_en?: string;
  html_fr?: string;
  dropCap?: boolean;
} & CollapsibleProps;

export type HeadingBlock = {
  id: string;
  type: "heading";
  level: 2 | 3;
  text: string;
  text_en?: string;
  text_fr?: string;
};

export type ListBlock = {
  id: string;
  type: "list";
  ordered: boolean;
  /** Arabic items (default). Items may contain inline HTML like <b>word</b>. */
  items: string[];
  items_en?: string[];
  items_fr?: string[];
} & CollapsibleProps;

export type AlertBlock = {
  id: string;
  type: "alert";
  variant: AlertVariant;
  title?: string;
  title_en?: string;
  title_fr?: string;
  html: string;
  html_en?: string;
  html_fr?: string;
} & CollapsibleProps;

export type QuoteBlock = {
  id: string;
  type: "quote";
  html: string;
  html_en?: string;
  html_fr?: string;
  author?: string;
  author_en?: string;
  author_fr?: string;
};

export type ImageBlock = {
  id: string;
  type: "image";
  src: string;
  alt?: string;
  alt_en?: string;
  alt_fr?: string;
  caption?: string;
  caption_en?: string;
  caption_fr?: string;
};

export type TableBlock = {
  id: string;
  type: "table";
  headers: string[];
  headers_en?: string[];
  headers_fr?: string[];
  rows: string[][];
  rows_en?: string[][];
  rows_fr?: string[][];
} & CollapsibleProps;

export type ArticleBlock =
  | ParagraphBlock
  | HeadingBlock
  | ListBlock
  | AlertBlock
  | QuoteBlock
  | ImageBlock
  | TableBlock;

export type Article = ArticleBlock[];

/* ---------- Per-language pickers ---------- */

export type ArticleLang = "ar" | "en" | "fr";

/**
 * Generic field picker with graceful fallback.
 * Returns `b[field_<lang>]` when set, otherwise falls back to English (when
 * asking for French), then to the Arabic default `b[field]`.
 */
export function pickLangField<T = unknown>(
  b: Record<string, unknown>,
  field: string,
  lang: ArticleLang,
): T | undefined {
  const ar = b[field] as T | undefined;
  if (lang === "ar") return ar;
  const en = b[`${field}_en`] as T | undefined;
  if (lang === "en") return (en ?? ar);
  const fr = b[`${field}_fr`] as T | undefined;
  return fr ?? en ?? ar;
}


export function newBlockId(): string {
  return (typeof crypto !== "undefined" && "randomUUID" in crypto)
    ? crypto.randomUUID()
    : `b-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function createBlock(type: ArticleBlock["type"]): ArticleBlock {
  const id = newBlockId();
  switch (type) {
    case "paragraph": return { id, type, html: "" };
    case "heading":   return { id, type, level: 2, text: "" };
    case "list":      return { id, type, ordered: false, items: [""] };
    case "alert":     return { id, type, variant: "info", html: "" };
    case "quote":     return { id, type, html: "" };
    case "image":     return { id, type, src: "" };
    case "table":     return { id, type, headers: ["", ""], rows: [["", ""]] };
  }
}

/* ---------- Sanitizer (allow-list, no deps) ---------- */

const ALLOWED_TAGS = new Set(["B", "STRONG", "I", "EM", "U", "A", "BR", "SPAN"]);
const ALLOWED_ATTRS: Record<string, string[]> = {
  A: ["href", "title", "target", "rel"],
};

function sanitizeNode(node: Node): Node | null {
  if (node.nodeType === 3 /* text */) return node.cloneNode();
  if (node.nodeType !== 1 /* element */) return null;
  const el = node as Element;
  const tag = el.tagName.toUpperCase();
  if (!ALLOWED_TAGS.has(tag)) {
    // unwrap: return a fragment of sanitized children
    const frag = document.createDocumentFragment();
    el.childNodes.forEach((c) => {
      const s = sanitizeNode(c);
      if (s) frag.appendChild(s);
    });
    return frag;
  }
  const clean = document.createElement(tag);
  const allowed = ALLOWED_ATTRS[tag] ?? [];
  for (const attr of Array.from(el.attributes)) {
    if (!allowed.includes(attr.name)) continue;
    if (attr.name === "href") {
      const v = attr.value.trim();
      if (!/^(https?:|mailto:|\/|#)/i.test(v)) continue;
      clean.setAttribute("href", v);
      clean.setAttribute("rel", "noopener noreferrer nofollow");
      clean.setAttribute("target", "_blank");
      continue;
    }
    clean.setAttribute(attr.name, attr.value);
  }
  el.childNodes.forEach((c) => {
    const s = sanitizeNode(c);
    if (s) clean.appendChild(s);
  });
  return clean;
}

export function sanitizeRichHtml(input: string): string {
  if (typeof document === "undefined") {
    // SSR fallback: strip all tags
    return String(input ?? "").replace(/<[^>]*>/g, "");
  }
  const wrapper = document.createElement("div");
  wrapper.innerHTML = String(input ?? "");
  const out = document.createElement("div");
  wrapper.childNodes.forEach((c) => {
    const s = sanitizeNode(c);
    if (s) out.appendChild(s);
  });
  return out.innerHTML;
}

/* ---------- Legacy migration ---------- */

export function paragraphsToArticle(paragraphs?: string[]): Article {
  return (paragraphs ?? [])
    .map((p) => p?.trim())
    .filter(Boolean)
    .map<ParagraphBlock>((text) => ({
      id: newBlockId(),
      type: "paragraph",
      html: escapeHtml(text!),
    }));
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!),
  );
}
